import api from './api';

export type TipoGatilho = 'RECORRENCIA' | 'REACTIVACAO' | 'CROSS_SELL' | 'ONBOARDING';
export type Canal = 'INAPP' | 'PUSH' | 'EMAIL' | 'SMS' | 'WHATSAPP';
export type EstadoHabito = 'ACTIVO' | 'IRREGULAR' | 'INACTIVO' | 'CHURN';

export const CANAIS: Canal[] = ['INAPP', 'PUSH', 'EMAIL', 'SMS', 'WHATSAPP'];

export interface RegraEngagement {
  id: string;
  codigo: string;
  nome: string;
  descricao: string | null;
  tipo: TipoGatilho;
  tipo_servico: string | null;
  activo: boolean;
  canais: Canal[];
  antecedencia_dias: number;
  tolerancia_dias: number;
  inactivo_apos_multiplo: number;
  cross_sell_de: string | null;
  cross_sell_para: string | null;
  onboarding_dias: number[] | null;
  min_pagamentos: number;
  min_confianca: number;
  intervalo_min_dias: number;
  intervalo_max_dias: number;
  cooldown_dias: number;
  hora_inicio: number;
  hora_fim: number;
  max_envios_dia: number;
  custo_max_diario_cve: number | null;
  template_sms_codigo: string | null;
  template_email_codigo: string | null;
  template_push_titulo: string | null;
  template_push_corpo: string | null;
  template_whatsapp_nome: string | null;
  template_inapp_titulo: string | null;
  template_inapp_corpo: string | null;
  prioridade: number;
}

export interface HabitoEngagement {
  id: string;
  user_id: string;
  cliente_id: string | null;
  nome: string | null;
  tipo_servico: string;
  servico_nome: string | null;
  servico_label: string;
  entidade_servico: string | null;
  referencia_mascarada: string | null;
  n_pagamentos: number;
  primeiro_em: string | null;
  ultimo_em: string | null;
  intervalo_mediano_dias: number | null;
  intervalo_mad_dias: number | null;
  valor_mediano: number | null;
  confianca: number;
  proximo_previsto_em: string | null;
  estado: EstadoHabito;
  calculado_em: string;
}

export interface HabitoStats {
  porEstado: Array<{ estado: string; n: number }>;
  porServico: Array<{
    tipo_servico: string;
    n: number;
    intervalo_mediano: number | null;
    confianca_media: number;
  }>;
  totais: { habitos: number; activos: number; previstos7d: number };
}

export interface EnvioEngagement {
  id: string;
  regra_id: string;
  regra_nome: string | null;
  regra_codigo: string | null;
  habito_id: string | null;
  user_id: string;
  tipo: string;
  tipo_servico: string | null;
  canal: Canal;
  status: 'ENVIADO' | 'FALHOU' | 'SUPRIMIDO';
  motivo_supressao: string | null;
  chave_dedup: string;
  mensagem_preview: string | null;
  erro: string | null;
  custo_cve: number;
  converteu: number;
  convertido_em: string | null;
  valor_convertido: number | null;
  enviado_em: string | null;
  criado_em: string;
}

export interface EnvioStats {
  porStatus: Array<{ status: string; n: number }>;
  porCanal: Array<{ canal: string; enviados: number; convertidos: number; custo_cve: number }>;
  porSupressao: Array<{ motivo: string; n: number }>;
  totais: {
    enviados: number;
    convertidos: number;
    taxa_conversao: number;
    custo_cve: number;
    custo_por_conversao: number | null;
  };
}

export interface DispatchSummary {
  regraId: string;
  regraCodigo: string;
  candidatos: number;
  enviados: number;
  suprimidos: number;
  falhas: number;
  custoCve: number;
  porCanal: Record<string, number>;
  porSupressao: Record<string, number>;
  detalhe: Array<{
    userId: string;
    nome: string | null;
    canal: Canal | null;
    status: string;
    motivoSupressao?: string;
    preview?: string;
    chaveDedup: string;
  }>;
}

export interface RecomputeResult {
  modo: 'completo' | 'incremental';
  utilizadores: number;
  pagamentos: number;
  grupos: number;
  habitosEscritos: number;
  habitosComCadencia: number;
}

const BASE = '/admin/marketing/engagement';

class MarketingEngagementService {
  // ── Operação ──────────────────────────────────────────────────────────
  async setup(): Promise<{ regras_criadas: number; message?: string }> {
    const res = await api.post(`${BASE}/setup`);
    return { ...res.data.data, message: res.data.message };
  }

  // ── Regras ────────────────────────────────────────────────────────────
  async listRegras(): Promise<RegraEngagement[]> {
    const res = await api.get<{ success: boolean; data?: RegraEngagement[] }>(`${BASE}/regras`);
    return res.data.data || [];
  }

  async updateRegra(id: string, payload: Partial<RegraEngagement>): Promise<RegraEngagement> {
    const res = await api.put(`${BASE}/regras/${id}`, payload);
    return res.data.data;
  }

  async toggleRegra(id: string): Promise<{ activo: boolean; message: string }> {
    const res = await api.put(`${BASE}/regras/${id}/toggle`);
    return { activo: res.data.data.activo, message: res.data.message };
  }

  async pausarTudo(): Promise<{ regras_desactivadas: number; message: string }> {
    const res = await api.post(`${BASE}/pausar-tudo`);
    return { regras_desactivadas: res.data.data.regras_desactivadas, message: res.data.message };
  }

  // ── Hábitos ───────────────────────────────────────────────────────────
  async listHabitos(params: {
    tipo_servico?: string;
    estado?: string;
    confianca_min?: number;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ data: HabitoEngagement[]; total: number }> {
    const res = await api.get(`${BASE}/habitos`, { params });
    return { data: res.data.data || [], total: res.data.pagination?.total ?? 0 };
  }

  async habitoStats(): Promise<HabitoStats> {
    const res = await api.get(`${BASE}/habitos/stats`);
    return res.data.data;
  }

  async recomputar(body: { completo?: boolean; horas?: number; meses?: number } = {}): Promise<{
    result: RecomputeResult;
    message: string;
  }> {
    const res = await api.post(`${BASE}/recomputar`, body);
    return { result: res.data.data, message: res.data.message };
  }

  // ── Envios ────────────────────────────────────────────────────────────
  async listEnvios(params: {
    regra_id?: string;
    canal?: string;
    status?: string;
    desde?: string;
    ate?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ data: EnvioEngagement[]; total: number }> {
    const res = await api.get(`${BASE}/envios`, { params });
    return { data: res.data.data || [], total: res.data.pagination?.total ?? 0 };
  }

  async envioStats(dias = 30): Promise<EnvioStats> {
    const res = await api.get(`${BASE}/envios/stats`, { params: { dias } });
    return res.data.data;
  }

  // ── Simulação e disparo ───────────────────────────────────────────────
  async simular(regraId: string, limite = 100): Promise<{ resumo: DispatchSummary; message: string }> {
    const res = await api.post(`${BASE}/simular/${regraId}`, { limite });
    return { resumo: res.data.data, message: res.data.message };
  }

  async disparar(
    regraId: string,
    body: { dry_run?: boolean; limite?: number; ignorar_quiet_hours?: boolean } = {}
  ): Promise<{ resumo: DispatchSummary; message: string }> {
    const res = await api.post(`${BASE}/disparar/${regraId}`, body);
    return { resumo: res.data.data, message: res.data.message };
  }

  async testar(
    regraId: string,
    target: { telefone?: string; email?: string }
  ): Promise<{ resultados: Record<string, any>; previews: Record<string, string> }> {
    const res = await api.post(`${BASE}/teste/${regraId}`, target);
    return res.data.data;
  }

  async atribuir(): Promise<{ message: string }> {
    const res = await api.post(`${BASE}/atribuir`);
    return { message: res.data.message };
  }
}

export default new MarketingEngagementService();
